import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  apiAgentGetAgents,
  apiAgentCreateAgent,
  apiAgentGetProfile,
  ApiRequestError,
  type AdminPartner,
} from '@/lib/api'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { PasswordInput } from '@/components/PasswordInput'
import { cn } from '@/lib/utils'
import {
  Plus,
  RefreshCw,
  AlertCircle,
  Copy,
  CheckCircle2,
  ArrowLeft,
  X,
  ChevronRight,
  Lock,
  UserRound,
} from 'lucide-react'
import { useT } from '@/contexts/language'

// ─── Validation ─────────────────────────────────────────────────────────────
const schema = z.object({
  code: z
    .string()
    .min(1, 'Code is required')
    .max(50)
    .regex(/^[a-zA-Z0-9_-]+$/, 'Only letters, numbers, _ and -'),
  name: z.string().min(1, 'Name is required').max(100),
  // agent share, whole percent chosen from a select (0..cap). master share is derived.
  commissionRate: z.string().regex(/^\d{1,3}$/, 'Pick a share'),
  // The agent's own login for the agent console — same rules the API enforces.
  username: z
    .string()
    .min(3, 'At least 3 characters')
    .max(50)
    .regex(/^[a-zA-Z0-9_-]+$/, 'Only letters, numbers, _ and -'),
  password: z.string().min(8, 'At least 8 characters').max(128),
  walletType: z.enum(['internal', 'seamless']),
  // Optional even for a seamless agent: its wallet API may not exist yet, and both values
  // can be filled in later from the agent's page.
  walletUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  walletApiKey: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

// ─── Helpers ────────────────────────────────────────────────────────────────
function statusVariant(s: string): 'success' | 'destructive' | 'warning' | 'outline' {
  if (s === 'active') return 'success'
  if (s === 'suspended') return 'destructive'
  if (s === 'inactive') return 'warning'
  return 'outline'
}

function ratePct(rate: string | null): string {
  if (rate === null || rate === '') return '—'
  return `${(parseFloat(rate) * 100).toFixed(0)}%`
}

// ─── One-time secret reveal ──────────────────────────────────────────────────
function SecretField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label className="font-mono text-xs text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <code className="flex-1 font-mono text-xs bg-muted px-3 py-2 rounded-[var(--radius-sm)] border border-border overflow-x-auto whitespace-nowrap text-foreground">
          {value}
        </code>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void copy()}
          className="shrink-0 font-mono text-xs gap-1.5"
        >
          {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  )
}

interface CreatedSecrets {
  code: string
  username: string
  nexbitApiKey: string
  launchSecret: string
}

function ApiKeyReveal({ created, onDone }: { created: CreatedSecrets; onDone: () => void }) {
  const t = useT()
  return (
    <div className="rounded-[var(--radius)] border border-success/40 bg-success/5 p-5 flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
        <div>
          <p className="font-mono text-sm font-bold text-success">
            Agent <span className="font-semibold">{created.code}</span> created!
          </p>
          <p className="font-mono text-xs text-muted-foreground mt-1">
            Copy both values now — they will not be shown again.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="font-mono text-xs text-muted-foreground">{t.agents.loginAccount}</span>
        <div className="flex items-center gap-2 rounded-[var(--radius-sm)] border border-border bg-card px-3 py-2">
          <UserRound className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
          <span className="font-mono text-sm font-semibold text-foreground">{created.username}</span>
        </div>
        <p className="font-mono text-[10px] text-muted-foreground">
          {t.agents.loginCreatedHint(window.location.host)}
        </p>
      </div>

      <SecretField label="Lotmatic API Key" value={created.nexbitApiKey} />
      <SecretField label="Launch Secret (used to sign launchToken — HMAC)" value={created.launchSecret} />

      <Button className="font-mono text-xs gap-1.5" onClick={onDone}>
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to agents
      </Button>
    </div>
  )
}

// ─── Field error line ────────────────────────────────────────────────────────
function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return (
    <p className="font-mono text-xs text-destructive flex items-center gap-1" role="alert">
      <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
      {message}
    </p>
  )
}

function SectionBar({ children }: { children: string }) {
  return (
    <div className="px-4 py-3 border-y border-border bg-secondary/40">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{children}</span>
    </div>
  )
}

// ─── Create form ─────────────────────────────────────────────────────────────
function CreateAgentForm({
  onCreated,
  onCancel,
  capPct,
}: {
  onCreated: (created: CreatedSecrets) => void
  onCancel: () => void
  capPct: number
}) {
  const t = useT()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { walletType: 'seamless', commissionRate: '0' },
  })

  const walletType = watch('walletType')
  const agentPct = parseInt(watch('commissionRate') || '0', 10)
  const masterPct = Math.max(0, capPct - agentPct) // derived, display-only

  async function onSubmit(values: FormValues) {
    setSubmitError(null)
    try {
      const res = await apiAgentCreateAgent({
        code: values.code,
        name: values.name,
        commissionRate: (parseInt(values.commissionRate, 10) / 100).toFixed(4),
        walletType: values.walletType,
        walletUrl: values.walletUrl || undefined,
        walletApiKey: values.walletApiKey || undefined,
        username: values.username,
        password: values.password,
      })
      onCreated({
        code: res.code,
        username: res.loginUsername ?? values.username,
        nexbitApiKey: res.nexbitApiKey,
        launchSecret: res.launchSecret,
      })
    } catch (err) {
      setSubmitError(err instanceof ApiRequestError ? err.message : t.agents.createError)
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="rounded-[var(--radius)] border border-border bg-card overflow-hidden mb-4"
      noValidate
    >
      <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {t.agents.createAgent}
        </span>
        <button
          type="button"
          onClick={onCancel}
          className={cn(
            'text-muted-foreground hover:text-foreground transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm',
          )}
          aria-label={t.common.cancel}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-4 flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="code" className="font-mono text-xs">
            {t.agents.code}
          </Label>
          <Input
            id="code"
            placeholder="my-agent"
            className="font-mono text-sm"
            aria-invalid={!!errors.code}
            {...register('code')}
          />
          <FieldError message={errors.code?.message} />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="name" className="font-mono text-xs">
            {t.common.displayName}
          </Label>
          <Input
            id="name"
            placeholder="My Agent Co."
            className="font-mono text-sm"
            aria-invalid={!!errors.name}
            {...register('name')}
          />
          <FieldError message={errors.name?.message} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="commissionRate" className="font-mono text-xs">
              หุ้นเอเจนต์ (%)
            </Label>
            <select
              id="commissionRate"
              className={cn(
                'h-9 rounded-[var(--radius-sm)] border border-input bg-background px-3 font-mono text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
              {...register('commissionRate')}
            >
              {Array.from({ length: capPct + 1 }, (_, i) => (
                <option key={i} value={String(i)}>
                  {i}%
                </option>
              ))}
            </select>
            <FieldError message={errors.commissionRate?.message} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="font-mono text-xs text-muted-foreground">
              หุ้นมาสเตอร์ (%)
              <span className="ml-1.5">คำนวณอัตโนมัติ</span>
            </Label>
            <div className="h-9 flex items-center gap-2 rounded-[var(--radius-sm)] border border-dashed border-border bg-muted/40 px-3">
              <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
              <span className="font-mono text-sm font-semibold text-foreground">{masterPct}%</span>
              <span className="font-mono text-[10px] text-muted-foreground ml-auto">
                เพดาน {capPct}% − {agentPct}%
              </span>
            </div>
          </div>
        </div>
      </div>

      <SectionBar>{t.agents.loginAccount}</SectionBar>

      <div className="p-4 flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="username" className="font-mono text-xs">
              {t.common.username}
            </Label>
            <Input
              id="username"
              placeholder="agent-p1"
              autoComplete="off"
              className="font-mono text-sm"
              aria-invalid={!!errors.username}
              {...register('username')}
            />
            <FieldError message={errors.username?.message} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password" className="font-mono text-xs">
              {t.common.password}
            </Label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              className="font-mono text-sm"
              aria-invalid={!!errors.password}
              showLabel={t.agents.showPassword}
              hideLabel={t.agents.hidePassword}
              {...register('password')}
            />
            <FieldError message={errors.password?.message} />
          </div>
        </div>
        <p className="font-mono text-[10px] text-muted-foreground">{t.agents.loginAccountHint}</p>
      </div>

      <SectionBar>{t.agents.walletConfig}</SectionBar>

      <div className="p-4 flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label className="font-mono text-xs">{t.agents.walletType}</Label>
          <div className="flex rounded-[var(--radius-sm)] border border-border overflow-hidden">
            {(['seamless', 'internal'] as const).map((wt) => (
              <label
                key={wt}
                className={cn(
                  'flex-1 flex items-center justify-center py-2 font-mono text-xs cursor-pointer transition-colors',
                  walletType === wt
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-muted-foreground hover:bg-secondary',
                )}
              >
                <input type="radio" value={wt} className="sr-only" {...register('walletType')} />
                {wt}
              </label>
            ))}
          </div>
          <p className="font-mono text-[10px] text-muted-foreground">
            {walletType === 'seamless' ? t.agents.walletHelpSeamless : t.agents.walletHelpInternal}
          </p>
        </div>

        {walletType === 'seamless' && (
          <>
            <p className="font-mono text-[10px] text-muted-foreground">{t.agents.walletOptionalHint}</p>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="walletUrl" className="font-mono text-xs">
                {t.agents.walletUrl}
              </Label>
              <Input
                id="walletUrl"
                type="url"
                placeholder="https://agent.example.com"
                className="font-mono text-sm"
                aria-invalid={!!errors.walletUrl}
                {...register('walletUrl')}
              />
              <FieldError message={errors.walletUrl?.message} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="walletApiKey" className="font-mono text-xs">
                {t.agents.walletApiKey}
                <span className="text-muted-foreground ml-1.5">({t.agents.walletApiKeyHint})</span>
              </Label>
              <PasswordInput
                id="walletApiKey"
                placeholder="agent-secret-key"
                autoComplete="off"
                className="font-mono text-sm"
                aria-invalid={!!errors.walletApiKey}
                showLabel="Show key"
                hideLabel="Hide key"
                {...register('walletApiKey')}
              />
              <FieldError message={errors.walletApiKey?.message} />
            </div>
          </>
        )}
      </div>

      <div className="px-4 pb-4 flex flex-col gap-2">
        {submitError && (
          <div
            className="flex items-start gap-2 p-3 border border-destructive/40 rounded-[var(--radius-sm)] bg-destructive/10"
            role="alert"
          >
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
            <p className="font-mono text-xs text-destructive">{submitError}</p>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1 font-mono text-xs"
            onClick={onCancel}
          >
            {t.common.cancel}
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 font-mono text-xs font-semibold uppercase tracking-wider"
          >
            {isSubmitting ? t.common.creating : t.agents.createAgent}
          </Button>
        </div>
      </div>
    </form>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────
type View = 'list' | 'create' | 'created'

export default function MasterAgentsPage() {
  const t = useT()
  const navigate = useNavigate()
  const [agents, setAgents] = useState<AdminPartner[]>([])
  // The master's own agent_share_rate is the ceiling it can hand out (whole percent).
  const [capPct, setCapPct] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [view, setView] = useState<View>('list')
  const [created, setCreated] = useState<CreatedSecrets | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [ags, me] = await Promise.all([apiAgentGetAgents(), apiAgentGetProfile()])
      setAgents(ags)
      setCapPct(Math.round(parseFloat(me.commissionRate) * 100))
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  function handleCreated(c: CreatedSecrets) {
    setCreated(c)
    setView('created')
  }

  function handleDone() {
    setCreated(null)
    setView('list')
    void load()
  }

  if (view === 'created' && created) {
    return (
      <div className="p-4 md:p-6 max-w-xl">
        <ApiKeyReveal created={created} onDone={handleDone} />
      </div>
    )
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-mono text-sm font-bold uppercase tracking-wider text-foreground">
            {t.agents.title}
          </h1>
          <p className="font-mono text-xs text-muted-foreground mt-0.5">
            {t.members.createdViaApiNotice}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void load()}
            disabled={loading}
            className="font-mono text-xs gap-1.5"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            {t.common.refresh}
          </Button>
          {view === 'list' && (
            <Button size="sm" className="font-mono text-xs gap-1.5" onClick={() => setView('create')}>
              <Plus className="h-3.5 w-3.5" />
              {t.agents.createAgent}
            </Button>
          )}
        </div>
      </div>

      {view === 'create' && (
        <CreateAgentForm capPct={capPct} onCreated={handleCreated} onCancel={() => setView('list')} />
      )}

      {error && (
        <div
          className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            {t.agents.title}
          </span>
          {!loading && (
            <span className="font-mono text-xs text-secondary-foreground/60">
              {agents.length} {t.reports.agent}
            </span>
          )}
        </div>

        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-14" />
                <Skeleton className="h-4 w-14" />
                <Skeleton className="ml-auto h-5 w-16" />
              </div>
            ))}
          </div>
        ) : agents.length === 0 ? (
          <div className="py-12 text-center">
            <p className="font-mono text-sm text-muted-foreground">{t.agents.noAgents}</p>
            {view === 'list' && (
              <Button size="sm" className="mt-4 font-mono text-xs gap-1.5" onClick={() => setView('create')}>
                <Plus className="h-3.5 w-3.5" />
                {t.agents.createAgent}
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.agents.code}</TableHead>
                <TableHead className="text-right">{t.reports.agentShare}</TableHead>
                <TableHead className="text-right">{t.reports.masterShare}</TableHead>
                <TableHead>{t.agents.walletType}</TableHead>
                <TableHead className="text-right">{t.common.status}</TableHead>
                <TableHead className="w-8" aria-hidden="true"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((a) => (
                <TableRow
                  key={a.id}
                  onClick={() => navigate(`/master/agents/${a.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`/master/agents/${a.id}`)
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`${a.name} — ${t.agents.title}`}
                  className="cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                >
                  <TableCell>
                    <p className="font-mono text-sm font-semibold">{a.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{a.code}</p>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-muted-foreground">
                    {ratePct(a.commissionRate)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-muted-foreground">
                    {ratePct(a.masterShareRate)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={a.walletType === 'seamless' ? 'default' : 'secondary'}
                      className="font-mono text-[10px]"
                    >
                      {a.walletType}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={statusVariant(a.status)} className="text-[10px]">
                      {a.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="w-8 text-muted-foreground">
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
